// Onboarding Document Client - OFD API
/* eslint-disable no-unused-vars */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios';
import { FileResponse } from './ofdTypes';
import { isAxiosError, throwException } from './ofdUtils';

type JsonParseReviver = (key: string, value: unknown) => unknown;

export class OnboardingDocumentClient {
  protected instance: AxiosInstance;
  protected baseUrl: string;
  protected jsonParseReviver?: JsonParseReviver;

  constructor(baseUrl?: string, instance?: AxiosInstance) {
    this.instance = instance || axios.create();
    this.baseUrl = baseUrl ?? '';
  }

  /**
     * @param documentId (optional) 
     * @param retailerId (optional) 
     * @return Success
     */
  download(documentId?: number | undefined, retailerId?: number | undefined, cancelToken?: CancelToken): Promise<FileResponse> {
    let url_ = this.baseUrl + '/OnboardingDocument/download?';
    if (documentId === null)
      throw new Error('The parameter \'documentId\' cannot be null.');
    else if (documentId !== undefined)
      url_ += 'documentId=' + encodeURIComponent('' + documentId) + '&';
    if (retailerId === null)
      throw new Error('The parameter \'retailerId\' cannot be null.');
    else if (retailerId !== undefined)
      url_ += 'retailerId=' + encodeURIComponent('' + retailerId) + '&';
    url_ = url_.replace(/[?&]$/, '');

    const options_: AxiosRequestConfig = {
      responseType: 'blob',
      method: 'GET',
      url: url_,
      headers: {
        'Accept': 'application/octet-stream',
      },
      cancelToken,
    };

    return this.instance.request(options_).catch((_error: unknown) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processDownload(_response);
    });
  }

  protected processDownload(response: AxiosResponse): Promise<FileResponse> {
    const status = response.status;
    const _headers: Record<string, string> = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = String(response.headers[k]);
        }
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers ? response.headers['content-disposition'] : undefined;
      let fileNameMatch = contentDisposition ? /filename\*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/g.exec(contentDisposition) : undefined;
      let fileName = fileNameMatch && fileNameMatch.length > 1 ? fileNameMatch[3] || fileNameMatch[2] : undefined;
      if (fileName) {
        fileName = decodeURIComponent(fileName);
      } else {
        fileNameMatch = contentDisposition ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition) : undefined;
        fileName = fileNameMatch && fileNameMatch.length > 1 ? fileNameMatch[1] : undefined;
      }
      return Promise.resolve({ fileName: fileName, status: status, data: new Blob([response.data], { type: response.headers['content-type'] }), headers: _headers });
    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException('An unexpected server error occurred.', status, _responseText, _headers);
    }
    return Promise.resolve<FileResponse>(null as unknown as FileResponse);
  }
}
